import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  reactive,
  ref,
  unref,
  watch,
  type ComponentPublicInstance,
} from 'vue';
import { elementLabelsById, ruleCycleSaveMessage } from '@record-fill';
import { useFormBuilderToast } from './useFormBuilderToast';
import { useModal } from 'vue-final-modal';
import ConfirmDeleteModal from '@/shared/ui/modals/ConfirmDeleteModal.vue';
import { useFormBuilderStore } from '../model/form-builder.store';
import {
  ACTIVE_RULE_CREATION_DRAFT_USAGE_ID,
  appendRuleOptionUsage,
  getAvailableRuleTriggerOptions,
  getPendingRuleCreationDraftUsageId,
  type RuleOptionUsage,
} from '../lib/rule-trigger-options';
import { flattenRuleFields, getRuleTargetOptions } from '../lib/rule-target-options';
import { RULE_ACTION_LABELS } from '../lib/report-fields-export';
import { useReportFieldsExport } from './useReportFieldsExport';
import {
  useFormRulesStore,
  BLANK_RULE_NAME_ERROR,
  isRuleCycleError,
  type ConditionalRuleAction,
  type ConditionalRuleTarget,
  type ConditionalThenBlock,
  type RuleCreationDraft,
  type RuleCreationDraftThenBlock,
} from '../model/form-rules.store';
import { useSavedFormRules } from './useSavedFormRules';
import type {
  DraftThenActionPayload,
  DraftThenBlock,
  DraftThenIndexedPayload,
  DraftThenTargetPayload,
} from './form-builder-rules-sidebar.types';

type RuleSortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc';
type DraftTargetSnapshot = Omit<ConditionalRuleTarget, 'fieldIds'> & {
  readonly fieldIds: readonly string[];
};
type DraftThenBlockSnapshot = Omit<RuleCreationDraftThenBlock, 'targets'> & {
  readonly targets: readonly DraftTargetSnapshot[];
};
type RuleCreationDraftSnapshot = Omit<RuleCreationDraft, 'thenBlocks'> & {
  readonly thenBlocks: readonly DraftThenBlockSnapshot[];
};

function formatRuleName(ruleNumber: number) {
  return `Regra ${String(ruleNumber).padStart(2, '0')}`;
}

function isBlankRuleNameError(error: unknown) {
  return error instanceof Error && error.message === BLANK_RULE_NAME_ERROR;
}

function cloneDraftTarget(target: DraftTargetSnapshot): ConditionalRuleTarget {
  return {
    ...target,
    fieldIds: [...target.fieldIds],
  };
}

function cloneDraftThenBlock(block: DraftThenBlockSnapshot): RuleCreationDraftThenBlock {
  return {
    ...block,
    targets: block.targets.map(cloneDraftTarget),
  };
}

function cloneRuleCreationDraft(draft: RuleCreationDraftSnapshot): RuleCreationDraft {
  return {
    ...draft,
    thenBlocks: draft.thenBlocks.map(cloneDraftThenBlock),
  };
}

function resetPendingThenBlock(block: RuleCreationDraft['thenBlocks'][number]) {
  return {
    ...block,
    action: '' as const,
    targets: [],
    confirmed: false,
  };
}

function updatePendingThenBlockAction(
  block: RuleCreationDraft['thenBlocks'][number],
  action: ConditionalRuleAction
) {
  return {
    ...block,
    action,
    confirmed: false,
  };
}

function removePendingThenBlockTarget(
  block: RuleCreationDraft['thenBlocks'][number],
  targetId: string
) {
  const targets = block.targets.filter((target) => target.id !== targetId);

  return {
    ...block,
    targets,
    confirmed: block.confirmed && targets.length > 0,
  };
}

function hasConfiguredRuleCreationDraft(draft: RuleCreationDraftSnapshot) {
  return draft.thenBlocks.some((block) => block.action || block.targets.length > 0);
}

function updateDraftThenBlock(
  draft: RuleCreationDraft,
  blockId: string,
  updater: (block: RuleCreationDraft['thenBlocks'][number]) => RuleCreationDraft['thenBlocks'][number]
) {
  return {
    ...draft,
    thenBlocks: draft.thenBlocks.map((block) => (block.id === blockId ? updater(block) : block)),
  };
}

function removeDraftThenBlock(draft: RuleCreationDraft, blockId: string) {
  return {
    ...draft,
    thenBlocks: draft.thenBlocks.filter((block) => block.id !== blockId),
    activeThenBlockId: draft.activeThenBlockId === blockId ? null : draft.activeThenBlockId,
  };
}

export function useFormBuilderRulesSidebar() {
  const searchTerm = ref('');
  const formBuilderStore = useFormBuilderStore();
  const formRulesStore = useFormRulesStore();
  const toast = useFormBuilderToast();

  const ruleName = ref(formatRuleName(Number(unref(formRulesStore.nextRuleNumber)) || 1));
  const selectedTriggerOption = ref('');
  const thenBlockSeed = ref(0);
  const thenBlocks = ref<DraftThenBlock[]>([]);
  const activeThenBlockId = ref<string | null>(null);
  const lastSelectionSignature = ref('');
  const isRuleExpanded = ref(true);
  const ruleCreationFormRef = ref<HTMLElement | null>(null);
  const isResettingDraftForm = ref(false);
  const selectedSortOption = ref<RuleSortOption | null>(null);
  const isSortActive = ref(false);
  const isSortDropdownOpen = ref(false);

  const confirmationMessages = {
    changeTriggerOption: {
      title: 'Tem certeza que deseja alterar a alternativa?',
      description: 'A ação não pode ser desfeita e as edições feitas serão perdidas.',
    },
    clearThenItems: {
      title: 'Tem certeza que deseja excluir todos os itens desta condição?',
      description: 'A ação não pode ser desfeita e os itens removidos serão perdidos.',
    },
    deleteRule: {
      title: 'Tem certeza que deseja excluir esta regra?',
      description:
        'A ação não pode ser desfeita e todas as condições e itens configurados serão perdidos.',
    },
  };

  const flattenedFields = computed(() => flattenRuleFields(formBuilderStore.elements));
  const triggerElement = computed(() => {
    const triggerId = formRulesStore.draftTriggerFieldId;
    if (!triggerId) return null;
    return flattenedFields.value.find((field) => field.id === triggerId) ?? null;
  });
  const ruleTriggerOptionUsages = computed<RuleOptionUsage[]>(() => {
    const usages: RuleOptionUsage[] = [...formRulesStore.rules];

    appendRuleOptionUsage(
      usages,
      ACTIVE_RULE_CREATION_DRAFT_USAGE_ID,
      formRulesStore.draftTriggerFieldId,
      selectedTriggerOption.value
    );

    formRulesStore.pendingCreationDrafts.forEach((draft, index) => {
      appendRuleOptionUsage(
        usages,
        getPendingRuleCreationDraftUsageId(index),
        draft.triggerFieldId,
        draft.triggerOption
      );
    });

    return usages;
  });
  const triggerOptions = computed(() => {
    const element = triggerElement.value;
    if (!element) return [];

    return getAvailableRuleTriggerOptions(
      element,
      ruleTriggerOptionUsages.value,
      ACTIVE_RULE_CREATION_DRAFT_USAGE_ID
    );
  });
  const triggerSelectOptions = computed(() =>
    triggerOptions.value.map((option) => ({
      id: option.value,
      name: option.label,
    }))
  );
  const actionSelectOptions = computed(() =>
    (['hide', 'disable', 'show'] as ConditionalRuleAction[]).map((id) => ({
      id,
      name: RULE_ACTION_LABELS[id],
    }))
  );
  const nextRuleName = computed(() =>
    formatRuleName(
      (Number(unref(formRulesStore.nextRuleNumber)) || 1) +
        (formRulesStore.pendingCreationDrafts?.length ?? 0)
    )
  );
  const targetOptions = computed<ConditionalRuleTarget[]>(() => {
    const triggerId = triggerElement.value?.id;
    return getRuleTargetOptions(formBuilderStore.elements, flattenedFields.value, triggerId);
  });
  const filteredRules = computed(() => {
    const term = searchTerm.value.trim().toLowerCase();
    const currentRules = formRulesStore.rules.map((rule) => ({
      ...rule,
      nameError: formRulesStore.hasDuplicateRuleNameError(rule.id),
      unsavedError: formRulesStore.hasIncompleteSavedRuleError(rule.id),
    }));
    const filtered = term
      ? currentRules.filter((rule) => rule.name.toLowerCase().includes(term))
      : currentRules;

    if (!selectedSortOption.value) {
      return filtered;
    }

    return filtered.sort((a, b) => {
      if (selectedSortOption.value === 'oldest') {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }

      if (selectedSortOption.value === 'name-asc') {
        return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
      }

      if (selectedSortOption.value === 'name-desc') {
        return b.name.localeCompare(a.name, 'pt-BR', { sensitivity: 'base' });
      }

      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  });
  const noSearchResults = computed(
    () => searchTerm.value.trim().length > 0 && filteredRules.value.length === 0
  );
  const pendingCreationDrafts = computed(() =>
    formRulesStore.pendingCreationDrafts.map((draft, index) => {
      const trigger = flattenedFields.value.find((field) => field.id === draft.triggerFieldId);
      const availableTriggerOptions = getAvailableRuleTriggerOptions(
        trigger,
        ruleTriggerOptionUsages.value,
        getPendingRuleCreationDraftUsageId(index)
      );

      return {
        ...draft,
        thenBlocks: draft.thenBlocks.map(cloneDraftThenBlock),
        triggerOptions: availableTriggerOptions.map((option) => ({
          id: option.value,
          name: option.label,
        })),
      };
    })
  );
  const isCreatingRule = computed(() => formRulesStore.isCreatingRule && !!triggerElement.value);
  const configuredThenBlocks = computed(() =>
    thenBlocks.value.filter((block) => block.action && block.targets.length > 0)
  );

  function createThenBlock(): DraftThenBlock {
    thenBlockSeed.value += 1;

    return {
      id: `then-${thenBlockSeed.value}`,
      action: '',
      targets: [],
      confirmed: false,
    };
  }

  function getSelectionSignature() {
    const selectedIds = formRulesStore.selectedFieldIds.join(',');
    const anchor = formRulesStore.menuAnchor;

    return `${formRulesStore.scopeType}:${selectedIds}:${JSON.stringify(anchor)}`;
  }

  function updateSortOption(option: RuleSortOption) {
    if (selectedSortOption.value === option && isSortActive.value) {
      selectedSortOption.value = null;
      isSortActive.value = false;
      isSortDropdownOpen.value = false;
      return;
    }

    selectedSortOption.value = option;
    isSortActive.value = true;
    isSortDropdownOpen.value = false;
  }

  function setSortDropdownOpen(open: boolean) {
    if (areRuleActionsDisabled.value) {
      isSortDropdownOpen.value = false;
      return;
    }

    isSortDropdownOpen.value = open;
  }

  function openConfirmationModal(title: string, description: string, onConfirm: () => void) {
    const { open, close } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        title,
        description,
        onClose() {
          close();
        },
        onConfirm() {
          close();
          onConfirm();
        },
      },
    });

    open();
  }

  const savedRules = useSavedFormRules({
    activeDraftThenBlockId: activeThenBlockId,
    confirmationMessages,
    getSelectionSignature,
    lastSelectionSignature,
    openConfirmationModal,
  });
  const activeSavedThenBlock = savedRules.activeThenBlock;
  const expandedRuleIds = savedRules.expandedRuleIds;
  const areRuleActionsDisabled = computed(
    () => formRulesStore.rules.length === 0 || formRulesStore.ruleItemSelectionActive
  );
  const reportFieldsExport = useReportFieldsExport();
  const isReportFieldsExportDisabled = computed(
    () => formRulesStore.reportFieldIds.length === 0 || formRulesStore.ruleItemSelectionActive
  );
  const globalRulesToggleLabel = computed(() =>
    savedRules.hasAnyExpanded.value ? 'Recolher tudo' : 'Mostrar tudo'
  );

  function resetDraftForm(clearSavedSelection = true) {
    isResettingDraftForm.value = true;
    if (clearSavedSelection) {
      activeSavedThenBlock.value = null;
    }
    const persistedDraft = formRulesStore.ruleCreationDraft;

    if (persistedDraft?.triggerFieldId === formRulesStore.draftTriggerFieldId) {
      ruleName.value = persistedDraft.ruleName || nextRuleName.value;
      selectedTriggerOption.value = persistedDraft.triggerOption;
      thenBlocks.value = persistedDraft.thenBlocks.map((block) => ({
        id: block.id,
        action: block.action,
        targets: block.targets.map((target) => ({
          ...target,
          fieldIds: [...target.fieldIds],
        })),
        confirmed: block.confirmed,
      }));
      activeThenBlockId.value = persistedDraft.activeThenBlockId;
      isRuleExpanded.value = persistedDraft.isExpanded;
      thenBlockSeed.value = Math.max(
        thenBlockSeed.value,
        ...thenBlocks.value
          .map((block) => Number(block.id.replace('then-', '')))
          .filter((value) => Number.isFinite(value))
      );
    } else {
      ruleName.value = nextRuleName.value;
      selectedTriggerOption.value = '';
      const firstBlock = createThenBlock();
      thenBlocks.value = [firstBlock];
      activeThenBlockId.value = null;
      isRuleExpanded.value = true;
    }

    if (thenBlocks.value.length === 0) {
      thenBlocks.value = [createThenBlock()];
      activeThenBlockId.value = null;
    }

    lastSelectionSignature.value = getSelectionSignature();
    nextTick(() => {
      isResettingDraftForm.value = false;
    });
  }

  function resetAllThenBlocksAfterTriggerChange() {
    const hasConfiguredThenBlock = thenBlocks.value.some(
      (block) => block.action || block.targets.length > 0
    );

    if (!hasConfiguredThenBlock) return;

    const firstBlock = createThenBlock();
    thenBlocks.value = [firstBlock];
    activeThenBlockId.value = null;
    lastSelectionSignature.value = getSelectionSignature();
    formRulesStore.clearSelection();
    formRulesStore.setDraftTargetFieldIds([]);
  }

  function hasConfiguredDraftThenBlock() {
    return thenBlocks.value.some((block) => block.action || block.targets.length > 0);
  }

  function removeThenBlock(blockId: string) {
    if (thenBlocks.value.length === 1) return;
    thenBlocks.value = thenBlocks.value.filter((block) => block.id !== blockId);
    if (activeThenBlockId.value === blockId) {
      activeThenBlockId.value = null;
    }
    toast.showSuccessToast({ title: 'Seção removida com sucesso', type: 'success' });
  }

  function addThenBlock() {
    if (thenBlocks.value.length >= 3) return;
    const lastBlock = thenBlocks.value[thenBlocks.value.length - 1];
    if (!lastBlock?.confirmed) return;

    const nextBlock = createThenBlock();
    thenBlocks.value = [...thenBlocks.value, nextBlock];
  }

  function getActiveThenBlock() {
    return thenBlocks.value.find((block) => block.id === activeThenBlockId.value) ?? null;
  }

  function getTargetForField(fieldId: string) {
    return targetOptions.value.find((target) => target.type === 'field' && target.id === fieldId);
  }

  function getSelectedTargets() {
    const triggerId = triggerElement.value?.id;

    if (formRulesStore.scopeType === 'all') {
      return targetOptions.value.filter((target) => target.type === 'field');
    }

    const menuAnchor = formRulesStore.menuAnchor;
    if (menuAnchor.kind === 'table-scope' && menuAnchor.scope === 'table') {
      const tableTarget = targetOptions.value.find(
        (target) => target.type === 'table' && target.id === menuAnchor.tableId
      );
      return tableTarget ? [tableTarget] : [];
    }

    return formRulesStore.selectedFields
      .filter((field) => field.id !== triggerId)
      .map((field) => getTargetForField(field.id))
      .filter((target): target is ConditionalRuleTarget => !!target);
  }

  function upsertTargets(block: DraftThenBlock, targets: ConditionalRuleTarget[]) {
    if (targets.length === 0) return;

    const existingIds = new Set(block.targets.map((target) => target.id));
    block.targets = [...block.targets, ...targets.filter((target) => !existingIds.has(target.id))];
    block.confirmed = false;
  }

  function removeTarget(block: DraftThenBlock, targetId: string) {
    const wasConfirmed = block.confirmed;
    block.targets = block.targets.filter((target) => target.id !== targetId);
    block.confirmed = wasConfirmed && block.targets.length > 0;

    if (block.targets.length === 0 && activeThenBlockId.value === block.id) {
      activeThenBlockId.value = null;
      formRulesStore.clearSelection();
      formRulesStore.setDraftTargetFieldIds([]);
    }

    toast.showSuccessToast({ title: 'Item removido com sucesso!', type: 'success' });
  }

  function buildRulePayload() {
    const persistedThenBlocks = configuredThenBlocks.value.map(toPersistedThenBlock);
    const targetFieldIds = Array.from(
      new Set(
        persistedThenBlocks.flatMap((block) => block.targets.flatMap((target) => target.fieldIds))
      )
    );

    return {
      name: ruleName.value,
      triggerFieldId: triggerElement.value?.id ?? '',
      triggerOption: selectedTriggerOption.value,
      action: persistedThenBlocks[0]?.action || ('show' as ConditionalRuleAction),
      targetFieldIds,
      thenBlocks: persistedThenBlocks,
    };
  }

  function confirmThenItems(block: DraftThenBlock) {
    if (activeThenBlockId.value !== block.id) return;

    if (block.targets.length === 0) {
      activeThenBlockId.value = null;
      formRulesStore.clearSelection();
      formRulesStore.setDraftTargetFieldIds([]);
      return;
    }

    block.confirmed = true;
    activeThenBlockId.value = null;
    formRulesStore.clearSelection();
    formRulesStore.setDraftTargetFieldIds([]);
    toast.showSuccessToast({ title: 'Itens adicionados com sucesso', type: 'success' });

    try {
      const rulePayload = buildRulePayload();
      const createdRule = formRulesStore.applyRule(rulePayload);
      expandedRuleIds.value = Array.from(new Set([...expandedRuleIds.value, createdRule.id]));
      formRulesStore.cancelRuleCreation();
      toast.showSuccessToast({ title: 'Regra aplicada com sucesso', type: 'success' });
    } catch (error) {
      if (isBlankRuleNameError(error)) {
        toast.showErrorToast({
          title: 'O nome da regra é obrigatório.',
          type: 'error',
        });
        savedRules.renderVersion.value += 1;
        return;
      }

      if (isRuleCycleError(error)) {
        toast.showErrorToast({
          title: ruleCycleSaveMessage(
            error.cycles.map((ids) => elementLabelsById(formBuilderStore.formStructure, ids))
          ),
          type: 'error',
        });
        savedRules.renderVersion.value += 1;
        return;
      }

      toast.showErrorToast({
        title: 'Erro ao aplicar regra. Tente novamente',
        type: 'error',
      });
    }
  }

  function updateThenAction(block: DraftThenBlock, value: string | number) {
    block.action = value as ConditionalRuleAction;
    block.confirmed = false;
  }

  function startDraftThenItemSelection(block: DraftThenBlock) {
    if (!selectedTriggerOption.value || !block.action || activeThenBlockId.value) return;

    expandedRuleIds.value = [];
    block.confirmed = false;
    activeThenBlockId.value = block.id;
    formRulesStore.clearSelection();
    lastSelectionSignature.value = getSelectionSignature();
  }

  function buildRuleCreationDraft(): RuleCreationDraft | null {
    const triggerFieldId = formRulesStore.draftTriggerFieldId;
    if (!triggerFieldId) return null;

    return {
      triggerFieldId,
      ruleName: ruleName.value,
      triggerOption: selectedTriggerOption.value,
      thenBlocks: thenBlocks.value.map((block) => ({
        id: block.id,
        action: block.action,
        targets: block.targets.map((target) => ({
          ...target,
          fieldIds: [...target.fieldIds],
        })),
        confirmed: block.confirmed,
      })),
      activeThenBlockId: activeThenBlockId.value,
      isExpanded: isRuleExpanded.value,
    };
  }

  function persistRuleCreationDraft() {
    if (isResettingDraftForm.value) return;
    formRulesStore.setRuleCreationDraft(buildRuleCreationDraft());
  }

  formRulesStore.setRuleCreationDraftProvider(buildRuleCreationDraft);

  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      formRulesStore.setRuleCreationDraftProvider(null);
    });
  }

  function toPersistedThenBlock(block: DraftThenBlock): ConditionalThenBlock {
    return {
      id: block.id,
      action: block.action as ConditionalRuleAction,
      targets: block.targets,
    };
  }

  function syncSelectedTargetsToActiveBlock() {
    if (savedRules.syncSelectedTargets()) return;

    const activeBlock = getActiveThenBlock();
    if (!selectedTriggerOption.value || !activeBlock?.action) return;
    upsertTargets(activeBlock, getSelectedTargets());
  }

  function removeDraftRule() {
    formRulesStore.cancelRuleCreation();
    resetDraftForm();
    toast.showSuccessToast({ title: 'Regra removida com sucesso', type: 'success' });
  }

  function requestDraftRuleRemoval() {
    openConfirmationModal(
      confirmationMessages.deleteRule.title,
      confirmationMessages.deleteRule.description,
      removeDraftRule
    );
  }

  function clearDraftThenBlock(block: DraftThenBlock) {
    block.action = '';
    block.targets = [];
    block.confirmed = false;
    activeThenBlockId.value = null;
    formRulesStore.clearSelection();
    formRulesStore.setDraftTargetFieldIds([]);
    toast.showSuccessToast({ title: 'Itens removidos com sucesso', type: 'success' });
  }

  function removeThenBlockOrRule(block: DraftThenBlock, index: number) {
    openConfirmationModal(
      confirmationMessages.clearThenItems.title,
      confirmationMessages.clearThenItems.description,
      () => {
        if (index === 0) {
          clearDraftThenBlock(block);
          return;
        }

        removeThenBlock(block.id);
      }
    );
  }

  function updateDraftRuleName(value: string) {
    ruleName.value = value;
  }

  function updateDraftExpanded(value: boolean) {
    isRuleExpanded.value = value;
  }

  function updateDraftTriggerOption(value: string | number) {
    const nextValue = String(value);
    if (nextValue === selectedTriggerOption.value) return;

    if (!hasConfiguredDraftThenBlock()) {
      selectedTriggerOption.value = nextValue;
      return;
    }

    openConfirmationModal(
      confirmationMessages.changeTriggerOption.title,
      confirmationMessages.changeTriggerOption.description,
      () => {
        selectedTriggerOption.value = nextValue;
      }
    );
  }

  function handleDraftThenAction(payload: DraftThenActionPayload) {
    updateThenAction(payload.block, payload.value);
  }

  function handleDraftThenDelete(payload: DraftThenIndexedPayload) {
    removeThenBlockOrRule(payload.block, payload.index);
  }

  function handleDraftTargetRemoval(payload: DraftThenTargetPayload) {
    removeTarget(payload.block, payload.targetId);
  }

  function getPendingDraftClone(index: number) {
    const draft = formRulesStore.pendingCreationDrafts[index];
    return draft ? cloneRuleCreationDraft(draft) : null;
  }

  function setPendingDraft(index: number, draft: RuleCreationDraft) {
    formRulesStore.updatePendingRuleCreationDraft(index, draft);
  }

  function updatePendingRuleName(index: number, value: string) {
    const draft = getPendingDraftClone(index);
    if (!draft) return;

    setPendingDraft(index, {
      ...draft,
      ruleName: value,
    });
  }

  function updatePendingExpanded(index: number, value: boolean) {
    const draft = getPendingDraftClone(index);
    if (!draft) return;

    setPendingDraft(index, {
      ...draft,
      isExpanded: value,
    });
  }

  function applyPendingTriggerOptionChange(index: number, value: string) {
    const draft = getPendingDraftClone(index);
    if (!draft) return;

    setPendingDraft(index, {
      ...draft,
      triggerOption: value,
      thenBlocks: [createThenBlock()],
      activeThenBlockId: null,
    });
  }

  function updatePendingTriggerOption(index: number, value: string | number) {
    const nextValue = String(value);
    const draft = formRulesStore.pendingCreationDrafts[index];
    if (!draft || draft.triggerOption === nextValue) return;

    if (!hasConfiguredRuleCreationDraft(draft)) {
      applyPendingTriggerOptionChange(index, nextValue);
      return;
    }

    openConfirmationModal(
      confirmationMessages.changeTriggerOption.title,
      confirmationMessages.changeTriggerOption.description,
      () => applyPendingTriggerOptionChange(index, nextValue)
    );
  }

  function handlePendingThenAction(index: number, payload: DraftThenActionPayload) {
    const blockId = payload.block.id;
    const action = payload.value as ConditionalRuleAction;
    const draft = getPendingDraftClone(index);
    if (!draft) return;

    setPendingDraft(
      index,
      updateDraftThenBlock(draft, blockId, (block) => updatePendingThenBlockAction(block, action))
    );
  }

  function applyPendingThenBlockRemoval(index: number, payload: DraftThenIndexedPayload) {
    const blockId = payload.block.id;
    const draft = getPendingDraftClone(index);
    if (!draft) return;

    const nextDraft =
      payload.index === 0
        ? {
            ...updateDraftThenBlock(draft, blockId, resetPendingThenBlock),
            activeThenBlockId: null,
          }
        : removeDraftThenBlock(draft, blockId);

    setPendingDraft(index, nextDraft);
  }

  function removePendingThenBlockOrRule(index: number, payload: DraftThenIndexedPayload) {
    openConfirmationModal(
      confirmationMessages.clearThenItems.title,
      confirmationMessages.clearThenItems.description,
      () => applyPendingThenBlockRemoval(index, payload)
    );
  }

  function handlePendingTargetRemoval(index: number, payload: DraftThenTargetPayload) {
    const blockId = payload.block.id;
    const draft = getPendingDraftClone(index);
    if (!draft) return;

    setPendingDraft(
      index,
      updateDraftThenBlock(draft, blockId, (block) =>
        removePendingThenBlockTarget(block, payload.targetId)
      )
    );
  }

  function addPendingThenBlock(index: number) {
    const draft = getPendingDraftClone(index);
    if (!draft || draft.thenBlocks.length >= 3) return;

    const lastBlock = draft.thenBlocks[draft.thenBlocks.length - 1];
    if (!lastBlock?.confirmed) return;

    setPendingDraft(index, {
        ...draft,
        thenBlocks: [...draft.thenBlocks, createThenBlock()],
    });
  }

  function startPendingThenItemSelection(index: number, block: DraftThenBlock) {
    if (!block.action) return;

    expandedRuleIds.value = [];
    formRulesStore.activatePendingRuleCreationDraft(index, block.id);
    formRulesStore.clearSelection();
    lastSelectionSignature.value = getSelectionSignature();
  }

  function removePendingRule(index: number) {
    openConfirmationModal(
      confirmationMessages.deleteRule.title,
      confirmationMessages.deleteRule.description,
      () => {
        formRulesStore.removePendingRuleCreationDraft(index);
        toast.showSuccessToast({ title: 'Regra removida com sucesso', type: 'success' });
      }
    );
  }

  function setRuleCreationFormRef(el: Element | ComponentPublicInstance | null) {
    ruleCreationFormRef.value = el instanceof HTMLElement ? el : null;
  }

  function confirmDraftThenBlockAt(index: number) {
    const block = thenBlocks.value[index];
    if (block) {
      confirmThenItems(block);
    }
  }

  watch(
    () => [formRulesStore.draftTriggerFieldId, formRulesStore.ruleCreationSessionId] as const,
    async () => {
      resetDraftForm(!!formRulesStore.draftTriggerFieldId);
      if (formRulesStore.draftTriggerFieldId) {
        expandedRuleIds.value = [];
        await nextTick();
        ruleCreationFormRef.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }
    },
    { immediate: true }
  );

  watch(triggerOptions, (options) => {
    if (!options.some((option) => option.value === selectedTriggerOption.value)) {
      selectedTriggerOption.value = '';
    }
  });

  watch(selectedTriggerOption, (nextOption, previousOption) => {
    if (isResettingDraftForm.value || !previousOption || nextOption === previousOption) return;
    resetAllThenBlocksAfterTriggerChange();
  });

  watch(
    () => getSelectionSignature(),
    (signature) => {
      if (signature === lastSelectionSignature.value) return;
      lastSelectionSignature.value = signature;
      syncSelectedTargetsToActiveBlock();
    }
  );

  watch(
    [activeThenBlockId, activeSavedThenBlock],
    ([draftBlockId, savedBlock]) => {
      formRulesStore.setDraftInteractionLocked(!!draftBlockId || !!savedBlock, true);
    },
    { immediate: true }
  );

  watch(
    () => formRulesStore.rules.length,
    (ruleCount) => {
      if (ruleCount === 0) {
        isSortDropdownOpen.value = false;
      }
    }
  );

  watch(
    thenBlocks,
    (blocks) => {
      const targetFieldIds = blocks.flatMap((block) =>
        block.targets.flatMap((target) => target.fieldIds)
      );
      formRulesStore.setDraftTargetFieldIds(targetFieldIds);
      persistRuleCreationDraft();
    },
    { deep: true }
  );

  watch([ruleName, selectedTriggerOption, activeThenBlockId, isRuleExpanded], () => {
    persistRuleCreationDraft();
  });

  const state = reactive({
    searchTerm,
    ruleName,
    selectedTriggerOption,
    thenBlocks,
    activeThenBlockId,
    activeSavedThenBlock,
    isRuleExpanded,
  });

  const toolbar = reactive({
    actionsDisabled: areRuleActionsDisabled,
    exportDisabled: isReportFieldsExportDisabled,
    isExporting: reportFieldsExport.isExporting,
    exportReportFields: reportFieldsExport.exportReportFields,
    hasExpandedRule: savedRules.hasAnyExpanded,
    toggleLabel: globalRulesToggleLabel,
    selectedSortOption,
    isSortActive,
    isSortDropdownOpen,
    updateSortOption,
    setSortDropdownOpen,
  });

  const options = reactive({
    trigger: triggerSelectOptions,
    action: actionSelectOptions,
  });

  const rules = reactive({
    filtered: filteredRules,
    noResultsFound: noSearchResults,
    pendingCreationDrafts,
    isCreating: isCreatingRule,
    isSavedEditingLocked: savedRules.editingLocked,
    renderVersion: savedRules.renderVersion,
  });

  const draft = {
    setFormRef: setRuleCreationFormRef,
    addThenBlock,
    addThenItems: startDraftThenItemSelection,
    confirmThenItems,
    updateRuleName: updateDraftRuleName,
    updateExpanded: updateDraftExpanded,
    updateTriggerOption: updateDraftTriggerOption,
    handleThenAction: handleDraftThenAction,
    handleThenDelete: handleDraftThenDelete,
    handleTargetRemoval: handleDraftTargetRemoval,
    confirmThenBlockAt: confirmDraftThenBlockAt,
    removeRule: requestDraftRuleRemoval,
  };

  const pending = {
    updateRuleName: updatePendingRuleName,
    updateExpanded: updatePendingExpanded,
    updateTriggerOption: updatePendingTriggerOption,
    handleThenAction: handlePendingThenAction,
    handleThenDelete: removePendingThenBlockOrRule,
    handleTargetRemoval: handlePendingTargetRemoval,
    addThenItems: startPendingThenItemSelection,
    addThenBlock: addPendingThenBlock,
    removeRule: removePendingRule,
  };

  const saved = {
    getTriggerSelectOptions: savedRules.getTriggerSelectOptions,
    toggleAll: savedRules.toggleAll,
    toggleRule: savedRules.toggleRule,
    isExpanded: savedRules.isExpanded,
    updateRuleName: savedRules.updateRuleName,
    updateTriggerOption: savedRules.updateTriggerOption,
    handleThenAction: savedRules.handleThenAction,
    addThenItems: savedRules.addThenItems,
    handleThenDelete: savedRules.handleThenDelete,
    confirmThenItems: savedRules.confirmThenItems,
    handleTargetRemoval: savedRules.handleTargetRemoval,
    addThenBlock: savedRules.addThenBlock,
    removeRule: savedRules.removeRule,
  };

  const actions = {
    syncSelectedTargetsToActiveBlock,
  };

  return {
    formRulesStore,
    state,
    toolbar,
    options,
    rules,
    draft,
    pending,
    saved,
    actions,
  };
}
